/**
 * useUpdateRole Hook
 *
 * React Query mutation for updating global role details (name, description, permissions).
 * Invalidates both roles list and specific role cache on success.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Invalidates all roles queries for the tenant on success
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { roleService } from '../../../api/role.service';
import type { RoleSanitized, RoleUpdateDTO } from '../../../types/role.types';

interface UpdateRoleVariables {
  roleId: string;
  data: RoleUpdateDTO;
}

/**
 * Update role mutation
 *
 * @param tenantId - Tenant ID
 * @returns Mutation result
 */
export const useUpdateRole = (
  tenantId: string
): UseMutationResult<RoleSanitized, Error, UpdateRoleVariables> => {
  const queryClient = useQueryClient();

  return useMutation<RoleSanitized, Error, UpdateRoleVariables>({
    mutationFn: ({ roleId, data }) => roleService.updateRole(tenantId, roleId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific role cache
      queryClient.invalidateQueries({ queryKey: ['role', tenantId, variables.roleId] });
      // Invalidate all roles queries for this tenant
      queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });
    },
    onError: (error) => {
      console.error('Failed to update role:', error);
    },
  });
};
